import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Users, 
  FileText, 
  Search, 
  Edit3, 
  History, 
  X, 
  Save, 
  ShieldCheck,
  AlertTriangle,
  Sparkles,
  Check
} from 'lucide-react';
import api from '../../../services/api';
import RequestHistoryModal from '../../../components/RequestHistoryModal';
import RequestDetailsModal from '../../../components/RequestDetailsModal';
import BulkGrantModal from '../../../components/BulkGrantModal';

const AdminLeaves = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(urlTab === 'balances' ? 'balances' : 'requests');

  useEffect(() => {
    if (urlTab === 'balances' || urlTab === 'requests') {
      setActiveTab(urlTab);
    }
  }, [urlTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'balances') {
      setSearchParams({ tab: 'balances' });
    } else {
      setSearchParams({});
    }
  };
  
  // Requests state
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [requestError, setRequestError] = useState('');
  
  // Balances state
  const [balances, setBalances] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [balanceSearchTerm, setBalanceSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [balanceError, setBalanceError] = useState('');
  
  // Modals state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [currentHistory, setCurrentHistory] = useState([]);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Balance Edit Modal state
  const [editBalanceModalOpen, setEditBalanceModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editFormData, setEditFormData] = useState({
    allocated: 12,
    used: 0,
    remaining: 12,
    reason: '',
    year: new Date().getFullYear(),
    leaveTypeId: ''
  });
  const [savingBalance, setSavingBalance] = useState(false);
  const [balanceModalError, setBalanceModalError] = useState('');

  const uniqueLeaveTypes = ['All Types', ...new Set(requests.map(r => r.leaveType?.name).filter(Boolean))];

  // Fetch Leave Requests
  const fetchRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await api.get('/leaves');
      setRequests(res.data);
    } catch (err) {
      setRequestError('Failed to fetch leave requests');
    } finally {
      setLoadingRequests(false);
    }
  };

  // Fetch Employee Leave Balances
  const fetchBalances = async (year = selectedYear) => {
    setLoadingBalances(true);
    setBalanceError('');
    try {
      const res = await api.get(`/leaves/balances/all?year=${year}`);
      setBalances(res.data);
    } catch (err) {
      setBalanceError(err.response?.data?.message || 'Failed to fetch employee leave balances');
    } finally {
      setLoadingBalances(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    fetchBalances(selectedYear);
  }, []);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    fetchBalances(year);
  };

  const [bulkGrantModalOpen, setBulkGrantModalOpen] = useState(false);
  const [selectedLeaveIds, setSelectedLeaveIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const handleUpdateStatus = async (id, status) => {
    if (!window.confirm(`Are you sure you want to mark this request as ${status}?`)) return;
    
    try {
      await api.put(`/leaves/${id}/status`, { status });
      await fetchRequests();
      // Also refresh balances in background
      fetchBalances(selectedYear);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleBulkUpdateLeaveStatus = async (status) => {
    if (selectedLeaveIds.length === 0) {
      alert('Please select at least one leave request.');
      return;
    }
    if (!window.confirm(`Are you sure you want to mark ${selectedLeaveIds.length} leave request(s) as ${status}?`)) return;

    setBulkProcessing(true);
    try {
      await api.put('/leaves/bulk-status', {
        requestIds: selectedLeaveIds,
        status
      });
      setSelectedLeaveIds([]);
      await fetchRequests();
      fetchBalances(selectedYear);
      alert(`Successfully ${status.toLowerCase()}ed ${selectedLeaveIds.length} leave request(s)!`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update leave requests');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Open Edit Balance Modal for an Employee
  const handleOpenEditBalance = (empRecord) => {
    const paidBal = empRecord.paidBalance || empRecord.balances?.find(b => b.leaveType?.isPaid) || empRecord.balances?.[0];
    setEditingEmployee(empRecord);
    const isEmpIntern = (empRecord.employee?.role || '').toLowerCase() === 'intern' || (empRecord.employee?.designation || '').toLowerCase().includes('intern');
    setEditFormData({
      allocated: isEmpIntern ? 0 : (paidBal?.allocated ?? 12),
      used: paidBal?.used ?? 0,
      remaining: isEmpIntern ? 0 : (paidBal?.remaining ?? 12),
      reason: '',
      year: selectedYear,
      leaveTypeId: paidBal?.leaveType?._id || ''
    });
    setBalanceModalError('');
    setEditBalanceModalOpen(true);
  };

  // Save updated balance
  const handleSaveBalance = async (e) => {
    e.preventDefault();
    if (!editingEmployee) return;

    const isEmpIntern = (editingEmployee.employee?.role || '').toLowerCase() === 'intern' || (editingEmployee.employee?.designation || '').toLowerCase().includes('intern');
    if (isEmpIntern && editFormData.allocated > 0) {
      setBalanceModalError('Interns are not eligible for paid leaves as per company policy.');
      return;
    }

    setSavingBalance(true);
    setBalanceModalError('');

    try {
      await api.put(`/leaves/balances/${editingEmployee.employee._id}`, editFormData);
      setEditBalanceModalOpen(false);
      await fetchBalances(selectedYear);
    } catch (err) {
      setBalanceModalError(err.response?.data?.message || 'Failed to update leave balance');
    } finally {
      setSavingBalance(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'APPROVED': return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
      case 'REJECTED': return 'bg-rose-100 text-rose-800 border border-rose-200';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800 border border-gray-200';
      default: return 'bg-amber-100 text-amber-800 border border-amber-200';
    }
  };

  const pendingRequestsCount = requests.filter(r => r.status === 'PENDING').length;

  // Filter requests
  const filteredRequests = requests.filter(r => {
    const term = searchTerm.toLowerCase();
    const matchSearch = 
      r.employeeId?.firstName?.toLowerCase().includes(term) ||
      r.employeeId?.lastName?.toLowerCase().includes(term) ||
      r.employeeId?.employeeId?.toLowerCase().includes(term) ||
      r.status?.toLowerCase().includes(term) ||
      r.leaveType?.name?.toLowerCase().includes(term);
    
    const matchStatus = statusFilter === 'All Status' || r.status === statusFilter;
    const matchType = typeFilter === 'All Types' || r.leaveType?.name === typeFilter;
    return matchSearch && matchStatus && matchType;
  });

  // Filter balances
  const filteredBalances = balances.filter(b => {
    const term = balanceSearchTerm.toLowerCase();
    const emp = b.employee || {};
    return (
      emp.firstName?.toLowerCase().includes(term) ||
      emp.lastName?.toLowerCase().includes(term) ||
      emp.employeeId?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term) ||
      emp.department?.name?.toLowerCase().includes(term) ||
      emp.designation?.toLowerCase().includes(term)
    );
  });

  // Stats calculation
  const totalEmployeesWithBalance = balances.length;
  const totalAllocatedLeaves = balances.reduce((acc, curr) => acc + (curr.paidBalance?.allocated || 0), 0);
  const totalUsedLeaves = balances.reduce((acc, curr) => acc + (curr.paidBalance?.used || 0), 0);
  const totalRemainingLeaves = balances.reduce((acc, curr) => acc + (curr.paidBalance?.remaining || 0), 0);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto w-full min-w-0">
      {/* Top Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Leave Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Manage employee leave requests and update yearly leave balances
            </p>
          </div>

          {/* Prominent Tab Switcher */}
          <div className="flex items-center bg-gray-100 p-1.5 rounded-2xl border border-gray-200 shadow-xs w-full sm:w-auto">
            <button
              onClick={() => handleTabChange('requests')}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${
                activeTab === 'requests'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Leave Requests</span>
              {pendingRequestsCount > 0 && (
                <span className="bg-amber-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">
                  {pendingRequestsCount}
                </span>
              )}
            </button>

            <button
              onClick={() => handleTabChange('balances')}
              className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${
                activeTab === 'balances'
                  ? 'bg-purple-700 text-white shadow-sm ring-1 ring-purple-600'
                  : 'text-purple-800 hover:text-purple-950 hover:bg-purple-50'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Employee Leave Balances (Edit Quotas)</span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                activeTab === 'balances' ? 'bg-purple-800 text-purple-100' : 'bg-purple-100 text-purple-800'
              }`}>
                {totalEmployeesWithBalance}
              </span>
            </button>
          </div>

          {/* Bulk Grant Button */}
          <button
            type="button"
            onClick={() => setBulkGrantModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all hover:shadow-md shrink-0 w-full sm:w-auto justify-center"
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>Bulk Grant WFH / Leave</span>
          </button>
        </div>
      </div>

      <BulkGrantModal
        isOpen={bulkGrantModalOpen}
        defaultType="LEAVE"
        onClose={() => setBulkGrantModalOpen(false)}
        onSuccess={() => {
          fetchRequests();
          fetchBalances(selectedYear);
        }}
      />

      {/* ========================================================== */}
      {/* TAB 1: LEAVE REQUESTS */}
      {/* ========================================================== */}
      {activeTab === 'requests' && (
        <div>
          {/* Quick Helper Banner to Switch to Balances */}
          <div className="mb-5 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 border border-purple-200 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-purple-950 text-sm">Looking to view or edit employee leave balances?</h4>
                <p className="text-xs text-purple-800">
                  Click the <strong>Employee Leave Balances</strong> tab above or use the button to directly adjust quotas and remaining days.
                </p>
              </div>
            </div>
            <button
              onClick={() => handleTabChange('balances')}
              className="shrink-0 bg-purple-700 hover:bg-purple-800 text-white font-semibold text-xs px-4 py-2 rounded-xl shadow-sm transition-all flex items-center gap-1.5"
            >
              <Edit3 className="w-3.5 h-3.5" />
              <span>Manage Leave Balances &rarr;</span>
            </button>
          </div>
          {/* Filters Bar */}
          <div className="flex gap-3 items-center flex-wrap mb-5 bg-white p-3.5 rounded-xl border border-gray-200 shadow-xs">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search by name, employee ID, status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3.5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white text-gray-700"
            >
              {uniqueLeaveTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3.5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm bg-white text-gray-700"
            >
              <option value="All Status">All Status</option>
              <option value="PENDING">PENDING</option>
              <option value="APPROVED">APPROVED</option>
              <option value="REJECTED">REJECTED</option>
              <option value="CANCELLED">CANCELLED</option>
            </select>
          </div>

          {requestError && (
            <div className="mb-5 bg-red-50 border-l-4 border-red-500 p-4 text-red-700 rounded-r-xl">
              {requestError}
            </div>
          )}

          {/* Requests Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-3 bg-gradient-to-r from-blue-50/70 to-indigo-50/70 border-b border-blue-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-blue-950">
                  Bulk Leave Actions:
                </span>
                <span className="text-xs text-blue-700">
                  {selectedLeaveIds.length} of {requests.filter(r => (r.adminStatus || r.hrStatus) === 'PENDING').length} pending selected
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={bulkProcessing || selectedLeaveIds.length === 0}
                  onClick={() => handleBulkUpdateLeaveStatus('APPROVED')}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Approve Selected ({selectedLeaveIds.length})</span>
                </button>
                <button
                  type="button"
                  disabled={bulkProcessing || selectedLeaveIds.length === 0}
                  onClick={() => handleBulkUpdateLeaveStatus('REJECTED')}
                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white rounded-xl text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Reject Selected ({selectedLeaveIds.length})</span>
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/80">
                  <tr>
                    <th className="px-4 py-3.5 text-center w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all pending leaves"
                        checked={(() => {
                          const pendings = requests.filter(r => (r.adminStatus || r.hrStatus) === 'PENDING');
                          return pendings.length > 0 && pendings.every(r => selectedLeaveIds.includes(r._id));
                        })()}
                        onChange={(e) => {
                          const pendings = requests.filter(r => (r.adminStatus || r.hrStatus) === 'PENDING');
                          if (e.target.checked) {
                            setSelectedLeaveIds(pendings.map(r => r._id));
                          } else {
                            setSelectedLeaveIds([]);
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Leave Type</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Dates</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Days Breakdown</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">HR Status</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Manager</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Final Status</th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loadingRequests ? (
                    <tr><td colSpan="10" className="px-6 py-8 text-center text-gray-500">Loading leave requests...</td></tr>
                  ) : filteredRequests.length === 0 ? (
                    <tr><td colSpan="10" className="px-6 py-8 text-center text-gray-500">No leave requests found matching filters</td></tr>
                  ) : (
                    filteredRequests.map(r => {
                      const isPending = (r.adminStatus || r.hrStatus) === 'PENDING';
                      const isChecked = selectedLeaveIds.includes(r._id);
                      return (
                      <tr key={r._id} className={`hover:bg-gray-50/60 transition-colors ${isChecked ? 'bg-blue-50/40' : ''}`}>
                        <td className="px-4 py-4 text-center">
                          {isPending ? (
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                setSelectedLeaveIds(prev => 
                                  prev.includes(r._id) ? prev.filter(x => x !== r._id) : [...prev, r._id]
                                );
                              }}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                            />
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white font-bold flex items-center justify-center text-xs uppercase shadow-xs">
                              {r.employeeId?.firstName?.[0] || 'E'}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-900">{r.employeeId?.firstName} {r.employeeId?.lastName}</div>
                              <div className="text-xs text-gray-400">{r.employeeId?.employeeId}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">{r.leaveType?.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(r.startDate).toLocaleDateString()} - {new Date(r.endDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex flex-col">
                            <span className="font-bold text-gray-900">{r.days} {r.days === 1 ? 'Day' : 'Days'}</span>
                            {(() => {
                              const isBothApproved = (r.managerStatus === 'APPROVED' && (r.adminStatus === 'APPROVED' || r.hrStatus === 'APPROVED'));
                              const isAdminApproved = r.adminStatus === 'APPROVED';
                              const isOverallApproved = r.status === 'APPROVED';

                              // If approved by both manager and admin, or final status is APPROVED for a paid leave type
                              if (isBothApproved || isAdminApproved || (isOverallApproved && (r.leaveType?.isPaid || r.leaveType?.name === 'Paid Leave' || r.leaveType?.name === 'Casual Leave'))) {
                                const paidCount = (r.paidDays !== undefined && Number(r.paidDays) > 0) ? r.paidDays : r.days;
                                const unpaidCount = (r.unpaidDays !== undefined && r.paidDays !== undefined && Number(r.paidDays) > 0) ? r.unpaidDays : Math.max(0, r.days - paidCount);
                                return (
                                  <div className="flex items-center gap-1.5 text-[11px] mt-0.5">
                                    <span className="text-emerald-700 font-semibold">{paidCount} Paid (Full Day)</span>
                                    {unpaidCount > 0 && (
                                      <span className="text-amber-700 font-semibold">| {unpaidCount} Unpaid</span>
                                    )}
                                  </div>
                                );
                              }

                              const isPaid = (r.paidDays > 0) || r.leaveType?.isPaid || r.leaveType?.name === 'Casual Leave' || r.leaveType?.name === 'Paid Leave' || (r.status === 'APPROVED');
                              if (isPaid) {
                                const paidCount = (r.paidDays !== undefined && Number(r.paidDays) > 0) 
                                  ? r.paidDays 
                                  : (r.status === 'APPROVED' ? r.days : (r.paidDays !== undefined && Number(r.paidDays) > 0 ? r.paidDays : r.days));
                                const unpaidCount = (r.unpaidDays !== undefined && r.paidDays !== undefined && Number(r.paidDays) > 0) ? r.unpaidDays : Math.max(0, r.days - paidCount);
                                return (
                                  <div className="flex items-center gap-1.5 text-[11px] mt-0.5">
                                    <span className="text-emerald-700 font-semibold">{paidCount} Paid</span>
                                    {unpaidCount > 0 && (
                                      <span className="text-amber-700 font-semibold">| {unpaidCount} Unpaid</span>
                                    )}
                                  </div>
                                );
                              }

                              return <span className="text-[11px] text-gray-500">Unpaid</span>;
                            })()}
                          </div>
                        </td>
                        <td 
                          className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate cursor-pointer hover:text-blue-600 hover:underline transition-colors" 
                          title="Click to view full reason"
                          onClick={() => {
                            setSelectedRequest(r);
                            setDetailsModalOpen(true);
                          }}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="truncate">{r.reason}</span>
                            <span className="text-[10px] bg-blue-50 text-blue-600 font-semibold px-1.5 py-0.5 rounded shrink-0">View</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadge(r.hrStatus || 'PENDING')}`}>
                            {r.hrStatus || 'PENDING'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadge(r.managerStatus || 'PENDING')}`}>
                            {r.managerStatus || 'PENDING'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadge(r.status)}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => {
                                const empBal = balances.find(b => b.employee?._id === r.employeeId?._id) || {
                                  employee: r.employeeId,
                                  paidBalance: { allocated: 12, used: 0, remaining: 12, leaveType: r.leaveType }
                                };
                                handleOpenEditBalance(empBal);
                              }}
                              className="text-purple-700 hover:text-purple-900 font-semibold text-xs bg-purple-50 hover:bg-purple-100 border border-purple-200 px-2.5 py-1 rounded-lg transition-colors flex items-center gap-1 shadow-xs"
                              title={`Edit leave balance for ${r.employeeId?.firstName || 'employee'}`}
                            >
                              <Edit3 className="w-3 h-3 text-purple-600" />
                              <span>Edit Balance</span>
                            </button>
                            <button
                              onClick={() => {
                                setCurrentHistory(r.history || []);
                                setHistoryModalOpen(true);
                              }}
                              className="text-gray-500 hover:text-blue-700 font-medium text-xs bg-gray-100 hover:bg-blue-50 px-2.5 py-1 rounded-lg transition-colors"
                            >
                              Track
                            </button>
                            {r.managerStatus === 'PENDING' && r.hrStatus === 'PENDING' && (
                              <div className="inline-flex gap-1.5">
                                <button 
                                  onClick={() => handleUpdateStatus(r._id, 'APPROVED')}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1 rounded-lg shadow-xs transition-colors"
                                >
                                  Approve
                                </button>
                                <button 
                                  onClick={() => handleUpdateStatus(r._id, 'REJECTED')}
                                  className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-2.5 py-1 rounded-lg shadow-xs transition-colors"
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================== */}
      {/* TAB 2: EMPLOYEE LEAVE BALANCES */}
      {/* ========================================================== */}
      {activeTab === 'balances' && (
        <div className="space-y-6">
          {/* Quick Metrics Banner */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Active Employees</span>
              <div className="text-2xl font-bold text-gray-900 mt-1">{totalEmployeesWithBalance}</div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-purple-200 shadow-xs">
              <span className="text-xs font-medium text-purple-600 uppercase tracking-wider">Total Paid Leaves Allocated</span>
              <div className="text-2xl font-bold text-purple-900 mt-1">{totalAllocatedLeaves} <span className="text-xs font-normal text-purple-600">days</span></div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-blue-200 shadow-xs">
              <span className="text-xs font-medium text-blue-600 uppercase tracking-wider">Leaves Used</span>
              <div className="text-2xl font-bold text-blue-900 mt-1">{totalUsedLeaves} <span className="text-xs font-normal text-blue-600">days</span></div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-emerald-200 shadow-xs">
              <span className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Remaining Balance</span>
              <div className="text-2xl font-bold text-emerald-900 mt-1">{totalRemainingLeaves} <span className="text-xs font-normal text-emerald-600">days</span></div>
            </div>
          </div>

          {/* Search and Year Bar */}
          <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-white p-3.5 rounded-xl border border-gray-200 shadow-xs">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search employees by name, ID, department..."
                value={balanceSearchTerm}
                onChange={(e) => setBalanceSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase">Year:</span>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(parseInt(e.target.value))}
                className="px-3.5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm bg-white font-semibold text-gray-800"
              >
                {[2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {balanceError && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700 rounded-r-xl">
              {balanceError}
            </div>
          )}

          {/* Balances Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-800 text-base">Annual Paid Leave Balances ({selectedYear})</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  12 paid leaves annually • Prorated from joining month for mid-year joiners • Max 4 paid leaves/month
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50/80">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Department & Role</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Joining Date</th>
                    <th className="px-6 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Allocated</th>
                    <th className="px-6 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Used</th>
                    <th className="px-6 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Remaining</th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loadingBalances ? (
                    <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading leave balances...</td></tr>
                  ) : filteredBalances.length === 0 ? (
                    <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">No employees found</td></tr>
                  ) : (
                    filteredBalances.map(item => {
                      const emp = item.employee;
                      const bal = item.paidBalance || {};
                      const isEmpIntern = (emp.role || '').toLowerCase() === 'intern' || (emp.designation || '').toLowerCase().includes('intern');
                      const allocated = isEmpIntern ? 0 : (bal.allocated ?? 12);
                      const used = bal.used ?? 0;
                      const remaining = isEmpIntern ? 0 : (bal.remaining ?? 12);

                      // Check if employee joined this year
                      let joinInfo = null;
                      if (emp.dateOfJoining) {
                        const jDate = new Date(emp.dateOfJoining);
                        const jYear = jDate.getFullYear();
                        if (jYear === selectedYear) {
                          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                          const jMonth = jDate.getMonth();
                          joinInfo = `Joined in ${monthNames[jMonth]} (${12 - jMonth} mos)`;
                        }
                      }

                      return (
                        <tr key={emp._id} className="hover:bg-purple-50/20 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-600 text-white font-bold flex items-center justify-center text-xs uppercase shadow-xs">
                                {emp.firstName?.[0] || 'E'}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">{emp.firstName} {emp.lastName}</div>
                                <div className="text-xs text-gray-400">{emp.employeeId || emp.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div>{emp.department?.name || 'General'}</div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-xs text-gray-400 capitalize">{emp.designation || emp.role}</span>
                              {isEmpIntern && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded border border-amber-200">
                                  Intern
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {emp.dateOfJoining ? (
                              <div>
                                <span>{new Date(emp.dateOfJoining).toLocaleDateString()}</span>
                                {joinInfo && (
                                  <div className="text-[11px] font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded-md inline-block mt-0.5 border border-indigo-100">
                                    {joinInfo}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">Not specified</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`font-bold text-sm px-2.5 py-1 rounded-lg ${
                              isEmpIntern ? 'bg-amber-50 text-amber-800 border border-amber-200 text-xs' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {isEmpIntern ? '0 (Intern)' : allocated}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="font-semibold text-blue-700 text-sm">
                              {used}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {isEmpIntern ? (
                              <span className="px-2.5 py-1 rounded-lg font-semibold text-xs bg-amber-50 text-amber-700 border border-amber-200">
                                Unpaid Only
                              </span>
                            ) : (
                              <span className={`px-3 py-1 rounded-lg font-bold text-sm shadow-xs ${
                                remaining >= 5 
                                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                                  : remaining > 0 
                                    ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                                    : 'bg-rose-100 text-rose-800 border border-rose-200'
                              }`}>
                                {remaining} Left
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => handleOpenEditBalance(item)}
                              className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold px-3.5 py-2 rounded-xl text-xs transition-all shadow-sm hover:shadow-md cursor-pointer"
                            >
                              <Edit3 className="w-3.5 h-3.5 text-purple-200" />
                              <span>Edit Balance</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================== */}
      {/* MODAL: UPDATE LEAVE BALANCE */}
      {/* ========================================================== */}
      {editBalanceModalOpen && editingEmployee && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-purple-600" />
                  Update Leave Balance
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Adjust paid leave balance directly for {editingEmployee.employee?.firstName} {editingEmployee.employee?.lastName}
                </p>
              </div>
              <button
                onClick={() => setEditBalanceModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-white/80 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveBalance} className="p-6 space-y-4">
              {/* Employee Summary Chip */}
              <div className="bg-gray-50 p-3.5 rounded-xl border border-gray-200 flex items-center justify-between text-xs">
                <div>
                  <span className="font-semibold text-gray-800">
                    {editingEmployee.employee?.firstName} {editingEmployee.employee?.lastName}
                  </span>
                  <div className="text-gray-500">{editingEmployee.employee?.employeeId} • {editingEmployee.employee?.department?.name || 'General'}</div>
                </div>
                <div className="text-right">
                  <span className="text-gray-500">Year: </span>
                  <strong className="text-purple-700">{selectedYear}</strong>
                  {editingEmployee.employee?.dateOfJoining && (
                    <div className="text-[11px] text-gray-400">
                      Joined: {new Date(editingEmployee.employee.dateOfJoining).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>

              {balanceModalError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-xl">
                  {balanceModalError}
                </div>
              )}

              {((editingEmployee.employee?.role || '').toLowerCase() === 'intern' || (editingEmployee.employee?.designation || '').toLowerCase().includes('intern')) && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 rounded-xl flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-950">Intern Leave Policy:</p>
                    <p>This employee is an Intern. Under company policy, interns are not eligible for paid leaves (Allocated quota is fixed at 0).</p>
                  </div>
                </div>
              )}

              {/* Input Fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Allocated Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    disabled={(editingEmployee.employee?.role || '').toLowerCase() === 'intern' || (editingEmployee.employee?.designation || '').toLowerCase().includes('intern')}
                    value={editFormData.allocated}
                    onChange={(e) => {
                      const newAlloc = parseFloat(e.target.value) || 0;
                      setEditFormData(prev => ({
                        ...prev,
                        allocated: newAlloc,
                        remaining: Math.max(0, newAlloc - prev.used)
                      }));
                    }}
                    className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-bold text-gray-800 text-center disabled:bg-gray-100 disabled:text-gray-400"
                  />
                  <span className="text-[10px] text-gray-400 mt-0.5 block text-center">
                    {((editingEmployee.employee?.role || '').toLowerCase() === 'intern' || (editingEmployee.employee?.designation || '').toLowerCase().includes('intern')) ? 'Intern: 0 paid leaves' : 'Standard: 12/yr'}
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Used Days
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={editFormData.used}
                    onChange={(e) => {
                      const newUsed = parseFloat(e.target.value) || 0;
                      setEditFormData(prev => ({
                        ...prev,
                        used: newUsed,
                        remaining: Math.max(0, prev.allocated - newUsed)
                      }));
                    }}
                    className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-bold text-blue-700 text-center"
                  />
                  <span className="text-[10px] text-gray-400 mt-0.5 block text-center">Approved taken</span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Remaining
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    required
                    value={editFormData.remaining}
                    onChange={(e) => {
                      setEditFormData(prev => ({
                        ...prev,
                        remaining: parseFloat(e.target.value) || 0
                      }));
                    }}
                    className="w-full p-2.5 border border-purple-300 bg-purple-50/50 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-bold text-purple-900 text-center"
                  />
                  <span className="text-[10px] text-purple-600 mt-0.5 block text-center">Available</span>
                </div>
              </div>

              {/* Adjustment Reason */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Reason for Adjustment / Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Mid-year joiner correction, special approval credit..."
                  value={editFormData.reason}
                  onChange={(e) => setEditFormData({ ...editFormData, reason: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>

              {/* Audit History of Past Updates */}
              {editingEmployee.paidBalance?.history?.length > 0 && (
                <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 max-h-36 overflow-y-auto">
                  <span className="text-[11px] font-semibold text-gray-600 uppercase flex items-center gap-1 mb-2">
                    <History className="w-3.5 h-3.5 text-gray-500" />
                    Past Balance Adjustments
                  </span>
                  <div className="space-y-2">
                    {editingEmployee.paidBalance.history.slice(-3).reverse().map((h, idx) => (
                      <div key={idx} className="text-xs bg-white p-2 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-center text-[11px] text-gray-500">
                          <span>{h.updatedBy ? `${h.updatedBy.firstName || ''} ${h.updatedBy.lastName || ''}`.trim() : 'Admin'}</span>
                          <span>{new Date(h.updatedAt).toLocaleDateString()}</span>
                        </div>
                        <div className="text-gray-700 mt-0.5">
                          Allocated: {h.previousAllocated} &rarr; <strong>{h.newAllocated}</strong>, Remaining: {h.previousRemaining} &rarr; <strong>{h.newRemaining}</strong>
                        </div>
                        {h.reason && <div className="text-[11px] text-purple-700 italic mt-0.5">"{h.reason}"</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Modal Actions */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setEditBalanceModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingBalance}
                  className="inline-flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2 rounded-xl text-sm shadow-md transition-all disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {savingBalance ? 'Saving...' : 'Save Balance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Track History & Details Modals */}
      <RequestHistoryModal 
        isOpen={historyModalOpen} 
        onClose={() => setHistoryModalOpen(false)} 
        history={currentHistory} 
      />
      <RequestDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        request={selectedRequest}
        type="LEAVE"
      />
    </div>
  );
};

export default AdminLeaves;
